import React from 'react';
import { View, Text, StyleSheet, TextInput } from 'react-native';
import Modal from '../PlatformModal';
import Button from '../Button';
import Dropdown from '../Dropdown';
import Switch from '../forms/Switch';
import AppTextInput from '../TextInputRef';
import colors from '../../config/colors';

/**
 * DeviceModals - Shared modal components for device screens
 * Contains all confirmation and settings modals
 */
function DeviceModals({
    // Device info
    deviceName,

    // Trigger modal
    visibleTrigger,
    handleTrigger,

    // Firmware modal
    visibleTriggerF,
    handleTriggerFirmwareUpdate,

    // Deregister modal
    visibleTrigger2,
    handleDeregister,

    // Rename modal
    renameModalRef,
    newDeviceNameRef,
    renameKeepConnect,

    // VPN modal (optional - not all devices have VPN)
    showVPN = true,
    visibleVPN,
    handleVPNsave,
    vpnSettings,
    setVpnSettings,
}) {
    return (
        <>
            {/* VPN Settings Modal */}
            {showVPN && (
                <Modal
                    isVisible={visibleVPN}
                    transparent={true}
                    animationType="slide"
                    onBackdropPress={() => handleVPNsave(false)}
                >
                    <View style={styles.vpnModalContainer}>
                        <View style={styles.vpnModalContent}>
                            <Text style={styles.modalTitle}>Enter VPN Settings {'\n'}</Text>
                            <Text style={styles.modalSubtitle}>Enter Device Password {'\n'}</Text>
                            <TextInput
                                secureTextEntry={true}
                                style={styles.textInput}
                                placeholder="Enter your password"
                                onChangeText={text => setVpnSettings(prevState => ({ ...prevState, vpnPassword: text }))}
                                value={vpnSettings?.vpnPassword || ''}
                            />
                            <Text style={styles.noteText}>
                                Note: This is the password entered into the device during setup.
                                It remains local to the device and is not related to your Cloud Services account password. {'\n'}
                            </Text>
                            <Dropdown
                                onTop={true}
                                labelText='Tunnel Type'
                                defaultValueProp={vpnSettings?.selectedTunnelType || 0}
                                itemsArray={[
                                    { label: 'LAN Traffic Only', value: 0 },
                                    { label: 'All Traffic', value: 1 },
                                ]}
                                onChangeItemProp={item => setVpnSettings(prevState => ({ ...prevState, selectedTunnelType: item }))}
                            />
                            <Dropdown
                                labelText='Connection Method'
                                defaultValueProp={vpnSettings?.selectedConnType || 0}
                                itemsArray={[
                                    { label: 'Auto (Relay)', value: 0 },
                                    { label: 'Direct (Port Forward)', value: 1 },
                                ]}
                                onChangeItemProp={item => setVpnSettings(prevState => ({ ...prevState, selectedConnType: item }))}
                            />
                            {vpnSettings?.selectedConnType === 1 && (
                                <TextInput
                                    keyboardType="numeric"
                                    style={styles.textInput}
                                    placeholder="Enter Device Port"
                                    onChangeText={text => setVpnSettings(prevState => ({ ...prevState, vpnPort: text }))}
                                    value={vpnSettings?.vpnPort || '4550'}
                                />
                            )}
                            <Switch
                                labelText='Store VPN Settings'
                                switchState={vpnSettings?.storeVPN !== false}
                                setSwitchState={(value) => setVpnSettings(prevState => ({ ...prevState, storeVPN: value }))}
                                isDisabled={false}
                            />
                            <Button title="Save" onPress={() => handleVPNsave(true)} />
                            <Button title="Cancel" onPress={() => handleVPNsave(false)} />
                        </View>
                    </View>
                </Modal>
            )}

            {/* Trigger Power Cycle Modal */}
            <Modal
                isVisible={visibleTrigger}
                transparent={true}
                animationType="slide"
                onBackdropPress={() => handleTrigger(false)}
            >
                <View style={styles.confirmModalContainer}>
                    <View style={styles.confirmModalContent}>
                        <Text style={styles.confirmText}>
                            Are you sure you want to power cycle:{'\n'}{deviceName}
                        </Text>
                        <Button title="Send It!" onPress={() => handleTrigger(true)} />
                        <Button title="Cancel" onPress={() => handleTrigger(false)} />
                    </View>
                </View>
            </Modal>

            {/* Firmware Update Modal */}
            <Modal
                isVisible={visibleTriggerF}
                transparent={true}
                animationType="slide"
                onBackdropPress={() => handleTriggerFirmwareUpdate(false)}
            >
                <View style={styles.confirmModalContainer}>
                    <View style={styles.confirmModalContent}>
                        <Text style={styles.confirmText}>
                            Check and Update Firmware for:{'\n'}{deviceName}
                        </Text>
                        <Button title="Send It!" onPress={() => handleTriggerFirmwareUpdate(true)} />
                        <Button title="Cancel" onPress={() => handleTriggerFirmwareUpdate(false)} />
                    </View>
                </View>
            </Modal>

            {/* Deregister Modal */}
            <Modal
                isVisible={visibleTrigger2}
                transparent={true}
                animationType="slide"
                onBackdropPress={() => handleDeregister(false)}
            >
                <View style={styles.confirmModalContainer}>
                    <View style={styles.confirmModalContent}>
                        <Text style={styles.confirmText}>
                            Are you sure you want to deregister:{'\n'}{deviceName}
                        </Text>
                        <Button title="Proceed" onPress={() => handleDeregister(true)} />
                        <Button title="Cancel" onPress={() => handleDeregister(false)} />
                    </View>
                </View>
            </Modal>

            {/* Rename Device Modal */}
            <Modal
                ref={renameModalRef}
                transparent={true}
                animationType="slide"
                onBackdropPress={() => renameModalRef.current?.closeModal()}
            >
                <View style={styles.renameModalContainer}>
                    <View style={styles.renameModalContent}>
                        <Text style={styles.modalTitle}>Enter New Device Name:</Text>
                        <AppTextInput
                            inputRef={newDeviceNameRef}
                            placeholder="Device Name"
                            style={styles.renameInput}
                        />
                        <Button title="Rename" onPress={renameKeepConnect} />
                        <Button title="Cancel" onPress={() => renameModalRef.current?.closeModal()} />
                    </View>
                </View>
            </Modal>
        </>
    );
}

const styles = StyleSheet.create({
    vpnModalContainer: {
        height: 700,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 20,
        backgroundColor: 'white',
    },
    vpnModalContent: {
        width: 300,
    },
    confirmModalContainer: {
        height: 200,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 20,
        backgroundColor: 'white',
    },
    confirmModalContent: {
        width: 300,
    },
    renameModalContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    renameModalContent: {
        backgroundColor: 'white',
        padding: 22,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 20,
        borderColor: 'rgba(0, 0, 0, 0.1)',
        maxHeight: '80%',
    },
    modalTitle: {
        fontWeight: 'bold',
        alignSelf: 'center',
    },
    modalSubtitle: {
        alignSelf: 'center',
    },
    noteText: {
        alignSelf: 'center',
        textAlign: 'center',
    },
    confirmText: {
        alignSelf: 'center',
        textAlign: 'center',
    },
    textInput: {
        height: 40,
        borderColor: 'gray',
        borderWidth: 1,
        paddingHorizontal: 10,
    },
    renameInput: {
        borderWidth: 1,
        borderColor: colors.light,
        borderRadius: 4,
        padding: 10,
        marginBottom: 10,
        width: '100%',
    },
});

export default DeviceModals;
